'use client'

import type { ReactNode } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'

import type { FooterCopy, NavbarCopy, SidebarCopy, SiteAuthCopy } from '@/lib/app-shell/copy'
import { useDisplayCurrency } from '@/lib/currency/display-currency'
import { LOCALE_DEFAULT_CURRENCY } from '@/lib/currency/map'
import type { CurrencyLabels } from '@/lib/currency/picker'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/app-sidebar'
import { BlogWordmark } from '@/components/blog-wordmark'
import { FooterContainer } from '@/components/footer-container'
import { Navbar } from '@/components/navbar'
import { buildCurrencyOptions } from '@/components/navigation/currency-menu-config'
import { buildLocaleOptions } from '@/components/navigation/locale-menu-config'
import {
    blogUrl,
    buildSiteAuth,
    buildSiteLogo,
    buildSiteMenu,
} from '@/components/navigation/menu-config'
import { Link, usePathname } from '@/i18n/navigation'

/** The shell's copy, rendered on the server: everything the chrome shows but the menu,
 *  which reads the message bundle. */
export interface AppLayoutCopy {
    readonly navigation: NavbarCopy & SidebarCopy & SiteAuthCopy
    readonly footer: FooterCopy
    readonly currencies: CurrencyLabels
}

interface AppLayoutProps {
    children: ReactNode
    /** Resolved by the server component that mounts this shell, and passed through. */
    copyrightYear: string
    copy: AppLayoutCopy
}

const NAVBAR_HIDDEN_ROUTES = ['/deal-me-a-trip']

export function AppLayout({ children, copyrightYear, copy }: AppLayoutProps) {
    const locale = useLocale()
    const t = useTranslations('navigation')
    // Built per render rather than module-level: the menu's labels and every link's
    // locale prefix follow the active locale.
    const siteLogo = buildSiteLogo(locale)
    const siteMenu = buildSiteMenu(locale, t)
    const siteAuth = buildSiteAuth(locale, copy.navigation)
    const pathname = usePathname()
    // The country picker's destinations are the current page in each locale, so they
    // are resolved here — this is where the locale, the locale-less path and the query
    // are all in hand — and passed down like the rest of the shell's configuration.
    //
    // The query is load-bearing, not tidiness: the travel review's required `token` and
    // `email` arrive as search parameters, so a destination built from the path alone
    // renders that form and then fails every submission (`locale-menu-config.ts`).
    // Reading it here is safe because no route in this app prerenders — every
    // one is server-rendered on demand, so `useSearchParams` has real values during SSR
    // and needs no Suspense boundary. A statically generated route would need one, and
    // Next fails that build loudly, saying so.
    //
    // The hook is nullable by contract — Next returns null wherever no route context
    // has resolved — and the picker must not be what takes the whole shell down if that
    // happens, so the destination degrades to its query-less form instead.
    const searchParams = useSearchParams()
    const selectedCurrency = useDisplayCurrency()
    const localeOptions = buildLocaleOptions(locale, pathname, searchParams?.toString())
    // The currency picker's destinations are the same page in each currency, so they
    // resolve from the same three inputs. The selection comes from the provider the
    // locale frame mounts, which is where the request's own currency arrives.
    const currencyOptions = buildCurrencyOptions(
        pathname,
        searchParams?.toString() ?? '',
        locale,
        selectedCurrency ?? LOCALE_DEFAULT_CURRENCY[locale],
        copy.currencies,
    )
    const hideNavbar = NAVBAR_HIDDEN_ROUTES.some((route) => pathname.includes(route))
    // Blog routes (/blog and everything under it) carry "The Window Seat"
    // masthead wordmark beside the logo in the otherwise-standard navbar.
    const isBlog = pathname.includes('/blog')

    return (
        <div className="flex min-h-screen w-full flex-col">
            {hideNavbar ? (
                <>
                    <header className="w-full border-b bg-background">
                        <div className="container mx-auto flex h-16 items-center px-4 md:px-6">
                            <Link href="/">
                                <Image
                                    src={siteLogo.src}
                                    width={siteLogo.width}
                                    height={siteLogo.height}
                                    alt={siteLogo.alt}
                                    className="h-8 w-auto"
                                />
                            </Link>
                        </div>
                    </header>
                    <main className="flex w-full flex-1 flex-col">{children}</main>
                </>
            ) : (
                <SidebarProvider defaultOpen={false} className="!min-h-0 flex-1">
                    <AppSidebar
                        copy={copy.navigation}
                        menu={siteMenu}
                        auth={siteAuth}
                        localeOptions={localeOptions}
                        currencyOptions={currencyOptions}
                        copyrightYear={copyrightYear}
                    />
                    <SidebarInset>
                        <Navbar
                            copy={copy.navigation}
                            logo={siteLogo}
                            menu={siteMenu}
                            auth={siteAuth}
                            localeOptions={localeOptions}
                            currencyOptions={currencyOptions}
                            brandSlot={isBlog ? <BlogWordmark /> : undefined}
                            brandUrl={isBlog ? blogUrl : undefined}
                        />
                        {/* SidebarInset is the page's <main>; a nested main is invalid HTML. */}
                        <div className="flex w-full flex-col">{children}</div>
                    </SidebarInset>
                </SidebarProvider>
            )}
            <FooterContainer copy={copy.footer} />
        </div>
    )
}
